import React, { Fragment, useContext, useMemo, useState, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import Select from 'react-select';
import { buildDeviceDropdownOption, getDeviceDropdownDid, getDeviceDropdownId } from '../../utils/dropdownLabels';
import { LanguageContext } from '../../context/LanguageContext';
import automationsTranslations from '../../languages/automation.json';
import { actionTypes, buildTemplatePinOptions } from './automationHelpers';
import { DeleteIcon } from '../../svg/UtilsIcons';

const resolveSelectedDeviceOption = (deviceOptions, action) =>
  deviceOptions.find((option) => option.value === action?.did) ||
  (action?.did
    ? buildDeviceDropdownOption(
      {
        id: action?.device_id,
        did: action?.did,
        name: action?.device_name,
      },
      'did'
    )
    : null);

const resolveSelectedPinOption = (pinOptions, action) =>
  pinOptions.find((option) => option.value === action?.pin_id) ||
  pinOptions.find((option) => option.name === action?.pin_name) ||
  null;

const AutomationPinSelect = ({ action, index, template, onUpdateAction }) => {
  const { lang } = useContext(LanguageContext);
  const t = automationsTranslations[lang];

  const pinOptions = useMemo(() => buildTemplatePinOptions(template), [template]);
  const selectedPinOption = useMemo(
    () => resolveSelectedPinOption(pinOptions, action),
    [action, pinOptions]
  );

  return (
    <div className='automation-action-wrapper'>
      <span className='automation-action-label'>{t.actionPin}</span>
      <Select
        id={`pin-${index}`}
        options={pinOptions}
        value={selectedPinOption}
        onChange={(selectedOption) =>
          onUpdateAction(index, {
            pin_id: selectedOption?.pin ?? null,
            pin_name: selectedOption?.name ?? null,
          })
        }
        classNamePrefix='react-select'
        isDisabled={!template}
        placeholder={t.actionPinPlaceholder || 'Select pin'}
        noOptionsMessage={() => t.noPinsAvailable || 'No pins available'}
      />
    </div>
  );
};

const AutomationActionCard = ({
  action,
  index,
  devices,
  actions,
  setActions,
  templatesByDid,
  selectedOptionDataStreams,
  trigger
}) => {
  const dataStreamsForSelect = useMemo(
    () =>
      selectedOptionDataStreams?.map((option) => ({
        value: option.pin,
        label: option.name,
      })) || [],
    [selectedOptionDataStreams]
  );
  const { lang } = useContext(LanguageContext);
  const t = automationsTranslations[lang];

  const actionTypeOptions = useMemo(
    () => actionTypes.map((option) => ({ value: option, label: option })),
    []
  );
  const deviceSelectOptions = useMemo(
    () => devices.map((device) => buildDeviceDropdownOption(device, 'did')),
    [devices]
  );

  const selectedActionTypeOption = actionTypeOptions.find((option) => option.value === action.type) || null;
  const selectedActionDeviceOption = resolveSelectedDeviceOption(deviceSelectOptions, action);
  const selectedTemplate = action?.did ? templatesByDid?.[action.did] ?? null : null;

  const updateAction = (patch) => {
    setActions(actions.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)));
  };
  const notificationTypes = [
    'Alarm',
    'Warning',
    'Fault',
    'Issue',
    'Recommendation',
    'Maintenance Reminder',
    'Anomaly Detection',
    'Energy Alert',
    'Water Alert',
    'Connectivity Alert',
    'Information',
    'Resolved',
    'Escalation',
  ];
  const notificationTypeOptions = useMemo(
    () =>
      notificationTypes.map((type) => ({
        value: type,
        label: type,
      })),
    []
  );
  const selectedNotificationTypeOption = notificationTypeOptions.find(
    (o) => o.value === action.notification_type
  ) || null;
  const [actionValueType, setActionValueType] = useState('constant');
  useEffect(() => {
    if (action.datastream_value&&trigger!=='schedule') {
      setActionValueType('datastream');
    } else {
      setActionValueType('constant');
    }
  }, [action.datastream_value,trigger]);
  return (
    <div className='details-curves-item' key={action.id ?? action.did ?? index}>
      <div className='automation-actions-item'>
        <button
          type='button'
          className='details-delete-btn auto-delete-btn'
          onClick={() => setActions(actions.filter((_item, itemIndex) => itemIndex !== index))}
        >
          <DeleteIcon />
        </button>

        <div className='automation-action-wrapper'>
          <span className='automation-action-label'>{t.actionType}</span>
          <Select
            id={`action-type-${index}`}
            options={actionTypeOptions}
            value={selectedActionTypeOption}
            onChange={(selectedOption) => {
              const nextType = selectedOption ? selectedOption.value : null;

              if (nextType === 'device') {
                updateAction({
                  type: nextType,
                  message: null,
                });
                return;
              }

              if (nextType === 'notification') {
                updateAction({
                  type: nextType,
                  device_id: null,
                  device_name: null,
                  did: null,
                  tid: null,
                  pin_id: null,
                  pin_name: null,
                  value: '',
                });
                return;
              }

              updateAction({
                type: nextType,
                device_id: null,
                device_name: null,
                did: null,
                tid: null,
                pin_id: null,
                pin_name: null,
                value: '',
                message: null,
              });
            }}
            classNamePrefix='react-select'
          />
        </div>

        {action.type === 'device' ? (
          <>
            <div className='automation-action-wrapper auto-action-device'>
              <span className='automation-action-label'>{t.actionDevice}</span>
              <Select
                id={`device-${index}`}
                options={deviceSelectOptions}
                value={selectedActionDeviceOption}
                onChange={(selectedOption) =>
                  updateAction({
                    device_id: getDeviceDropdownId(selectedOption),
                    device_name: selectedOption?.name || selectedOption?.baseLabel || null,
                    did: selectedOption?.value ?? null,
                    tid: selectedOption?.tid ?? null,
                    pin_id: null,
                    pin_name: null,
                    value: '',
                  })
                }
                classNamePrefix='react-select'
              />
            </div>

            {action.device_id ? (
              <>
                <AutomationPinSelect
                  action={action}
                  index={index}
                  template={selectedTemplate}
                  onUpdateAction={(_index, patch) => updateAction(patch)}
                />
                {/* <div className='edit-content-search auto-action-input-wrap'>
                  <input
                    className='auto-action-input'
                    type='text'
                    placeholder={t.placeholderNewValue}
                    value={action.value ?? ''}
                    onChange={(event) => updateAction({ value: event.target.value })}
                  />
                </div> */}
                <div className='automation-action-wrapper'>
                  <span className='automation-action-label '>Value Type</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 20 }} >
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        fontSize: 15,
                        fontWeight: 500,
                      }}
                    >
                      <input
                        type='radio'
                        name='actionValueType'
                        value='constant'
                        checked={actionValueType === 'constant'}
                        onChange={(event) => {
                          setActionValueType(event.target.value)
                        }}
                        style={{
                          transform: 'scale(1.3)',
                          cursor: 'pointer',
                        }}
                      />
                      <span className='automation-action-label '>Constant</span>
                    </label>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        fontSize: 15,
                        fontWeight: 500,
                      }}
                    >
                      <input
                        disabled={trigger === 'schedule'}
                        type='radio'
                        name='actionValueType'
                        value='datastream'
                        checked={actionValueType === 'datastream'}
                        onChange={(event) => {
                          setActionValueType(event.target.value)

                        }}
                        style={{
                          transform: 'scale(1.3)',
                          cursor: 'pointer',
                        }}
                      />
                      <span>Datastream</span>
                    </label>
                  </div>

                  {actionValueType === 'constant' ?
                    <Fragment>
                      <span className='automation-action-label '>Value</span>

                      <div className='qb-value ' >
                        <input
                          className='auto-action-input'
                          style={{ width: 'auto' }}
                          type='text'
                          placeholder={t.placeholderNewValue}
                          value={action.value ?? ''}
                          onChange={(event) => updateAction({ value: event.target.value, datastream_value: null, })}
                        />
                      </div></Fragment>
                    : <Fragment>
                      <span className='automation-action-label '>Value</span>
                      <Select
                        value={
                          dataStreamsForSelect.find(
                            (option) => option.value === action.datastream_value
                          ) || null
                        }
                        options={dataStreamsForSelect}
                        onChange={(opt) =>
                          updateAction({
                            datastream_value: opt?.value,
                            value: null,

                          })
                        }
                        style={{ width: 'inherit' }}
                        classNamePrefix='react-select'

                      />
                    </Fragment>

                  }
                </div>
              </>
            ) : null}
          </>
        ) : null}

        {action.type === 'notification' ? (
          <div style={{ display: 'flex', gap: 24 }} className='automation-action-wrapper'>
            <div className='edit-form-item auto-form-item'>
              <span className='automation-action-label'>Notification Type</span>

              <Select
                id={`notification-type-${index}`}
                options={notificationTypeOptions}
                value={selectedNotificationTypeOption}
                onChange={(selectedOption) =>
                  updateAction({
                    notification_type: selectedOption?.value || null,
                  })
                }
                classNamePrefix='react-select'
              />
            </div>
            <div className='edit-form-item auto-form-item'>
              <span className='automation-action-label'>Message</span>
              <textarea
                className='edit-form-input auto-desc-textarea'
                name='automation-description'
                placeholder={t.placeholderNotification}
                value={action.message ?? ''}
                onChange={(event) =>
                  updateAction({ message: event.target.value })
                } s
              />
            </div>

          </div>
        ) : null}
      </div>
    </div>
  );
};

const AutomationActionsBuilder = ({
  devices,
  actions,
  setActions,
  templatesByDid,
  selectedOptionDataStreams,
  trigger
}) => {
  const { lang } = useContext(LanguageContext);
  const t = automationsTranslations[lang];

  return (
    <div className='automation-actions-container'>
      <button
        type='button'
        className='details-add-btn'
        onClick={() => setActions([...(actions || []), { type: '' }])}
      >
        <span><AddIcon /></span>
        <span>{t.addButton}</span>
      </button>

      {(actions || []).map((action, index) => (
        <AutomationActionCard
          key={action?.id ?? `${getDeviceDropdownDid(action) || 'action'}-${index}`}
          action={action}
          index={index}
          devices={devices}
          actions={actions}
          setActions={setActions}
          templatesByDid={templatesByDid}
          selectedOptionDataStreams={selectedOptionDataStreams}
          trigger={trigger}
        />
      ))}
    </div>
  );
};

export default AutomationActionsBuilder;
